import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Receipt } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useToast } from '../ui/Toast';
import { api, type Wallet, type Client } from '../../lib/api';
import { TxHashField } from './TxHashField';
import { ClientSearchAutocomplete } from './ClientSearchAutocomplete';
import { CreateClientModal } from './CreateClientModal';
import { isCashierEntityAllowed } from './constants';

interface ExpenseFormProps {
    onSubmit: () => void;
}

const expenseFormSchema = z.object({
    amount: z
        .string()
        .min(1, 'Amount is required')
        .refine(
            (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
            'Must be a positive number'
        ),
    walletId: z.string().min(1, 'Please select a source wallet'),
    date: z.string().optional(),
    comment: z.string().max(200).optional(),
    category: z.string().min(1, 'Category is required'),
    txHash: z.string().optional(),
    employeeName: z.string().optional(),
});

type ExpenseFormData = z.infer<typeof expenseFormSchema>;

interface ExpenseCategoryOption {
    id: string;
    name: string;
    groupType: string;
}

const CURRENCY_ICONS: Record<string, string> = {
    RUB: '₽',
    EUR: '€',
    USD: '$',
    GBP: '£',
    USDT: '₮',
    BTC: '₿',
    ETH: 'Ξ',
    USDC: '$',
};

export function ExpenseForm({ onSubmit }: ExpenseFormProps) {
    const { t } = useTranslation();
    const { currentEntity } = useEntity();
    const toast = useToast();

    const [wallets, setWallets] = useState<Wallet[]>([]);
    const [walletsLoading, setWalletsLoading] = useState(true);
    const [categories, setCategories] = useState<ExpenseCategoryOption[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [employeeError, setEmployeeError] = useState<string | undefined>();
    const [createEmployeeFor, setCreateEmployeeFor] = useState<string | null>(null);
    const [autocompleteKey, setAutocompleteKey] = useState(0);

    const {
        register,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { errors },
    } = useForm<ExpenseFormData>({
        resolver: zodResolver(expenseFormSchema),
        defaultValues: {
            amount: '',
            walletId: '',
            date: new Date().toISOString().split('T')[0],
            comment: '',
            category: '',
            txHash: '',
            employeeName: '',
        },
    });

    const walletId = watch('walletId');
    const categoryName = watch('category');
    const employeeName = watch('employeeName');

    // Derive currency from selected wallet
    const selectedWallet = wallets.find((w) => w.id === walletId);
    const currency = selectedWallet?.currencyCode || '';

    const selectedCategory = categories.find((c) => c.name === categoryName);
    const isSalary = selectedCategory?.groupType === 'salary';

    const fetchWallets = useCallback(async () => {
        if (!currentEntity?.id) return;
        if (!isCashierEntityAllowed(currentEntity.code)) {
            setWallets([]);
            setWalletsLoading(false);
            return;
        }
        try {
            setWalletsLoading(true);
            const result = await api.admin.wallets.list(currentEntity.id);
            setWallets(result.data.filter((w) => w.isActive));
        } catch {
            toast.error('Failed to load wallets');
        } finally {
            setWalletsLoading(false);
        }
    }, [currentEntity?.id, currentEntity?.code, toast]);

    const fetchCategories = useCallback(async () => {
        try {
            setCategoriesLoading(true);
            const res = await fetch('/api/admin/categories', { credentials: 'include' });
            if (!res.ok) throw new Error('Failed to load categories');
            const result = await res.json();
            const active: ExpenseCategoryOption[] = result.data
                .filter((c: any) => c.isActive)
                .map((c: any) => ({ id: c.id, name: c.name, groupType: c.groupType || 'other' }));
            setCategories(active);
        } catch {
            toast.error('Failed to load categories');
        } finally {
            setCategoriesLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchWallets();
        fetchCategories();
    }, [fetchWallets, fetchCategories]);

    // Clear employee when category switches away from salary
    useEffect(() => {
        if (!isSalary && employeeName) {
            setValue('employeeName', '');
            setEmployeeError(undefined);
        }
    }, [isSalary, employeeName, setValue]);

    const onFormSubmit = async (data: ExpenseFormData) => {
        if (!currentEntity?.id) {
            toast.error('No entity selected');
            return;
        }

        const categoryMeta = categories.find((c) => c.name === data.category);
        const salary = categoryMeta?.groupType === 'salary';

        if (salary && !data.employeeName?.trim()) {
            setEmployeeError(t('cashier.createOperation.expenseEmployeeRequired', 'Please select an employee'));
            return;
        }

        setIsSubmitting(true);
        try {
            await api.transactions.create(currentEntity.id, {
                type: 'expense',
                amount: data.amount,
                currency,
                walletId: data.walletId,
                category: data.category,
                ...(data.comment && { comment: data.comment }),
                ...(data.date && { transactionDate: data.date }),
                ...(data.txHash && { txHash: data.txHash }),
                ...(salary && data.employeeName ? { clientId: data.employeeName } : {}),
            });

            toast.success(t('cashier.createOperation.expenseSuccess', 'Expense recorded'));

            reset({
                amount: '',
                walletId: data.walletId,
                date: new Date().toISOString().split('T')[0],
                comment: '',
                category: '',
                txHash: '',
                employeeName: '',
            });
            setEmployeeError(undefined);

            onSubmit();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to record expense');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-5">
            {/* Amount + Source Wallet */}
            <div>
                <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                    {t('cashier.createOperation.expenseAmount', 'Amount Spent')}
                </label>
                <div className="flex gap-3">
                    {/* Amount input */}
                    <div className="flex-1">
                        <div className="relative">
                            {currency && (
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground-muted pointer-events-none">
                                    {CURRENCY_ICONS[currency.toUpperCase()] || currency}
                                </span>
                            )}
                            <input
                                type="text"
                                inputMode="decimal"
                                className={`
                  w-full py-3 border rounded-lg text-sm text-foreground bg-surface
                  focus:outline-none focus:border-ctp-peach placeholder:text-foreground-muted
                  ${currency ? 'pl-8 pr-3' : 'px-3'}
                  ${errors.amount ? 'border-ctp-red' : 'border-border'}
                `}
                                placeholder="0.00"
                                {...register('amount')}
                            />
                        </div>
                        {errors.amount && (
                            <p className="text-[11px] text-ctp-red mt-1">{errors.amount.message}</p>
                        )}
                    </div>

                    {/* Wallet / Source selector */}
                    <div className="w-44">
                        <select
                            className={`
                w-full py-3 px-3 border rounded-lg text-sm text-foreground bg-surface
                focus:outline-none focus:border-ctp-peach
                ${errors.walletId ? 'border-ctp-red' : 'border-border'}
              `}
                            {...register('walletId')}
                        >
                            <option value="">
                                {walletsLoading
                                    ? t('loading', 'Loading...')
                                    : t('cashier.createOperation.expenseSource', 'Source')}
                            </option>
                            {wallets.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {CURRENCY_ICONS[w.currencyCode.toUpperCase()] || w.currencyCode}{' '}
                                    {w.name}
                                </option>
                            ))}
                        </select>
                        {errors.walletId && (
                            <p className="text-[11px] text-ctp-red mt-1">{errors.walletId.message}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Category */}
            <div>
                <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                    {t('cashier.createOperation.expenseCategory', 'Category')}
                </label>
                <select
                    className={`
            w-full py-2.5 px-3 border rounded-lg text-sm text-foreground bg-surface
            focus:outline-none focus:border-ctp-peach
            ${errors.category ? 'border-ctp-red' : 'border-border'}
          `}
                    {...register('category')}
                >
                    <option value="">
                        {categoriesLoading
                            ? t('loading', 'Loading...')
                            : t('cashier.createOperation.expenseCategoryPlaceholder', 'Select category...')}
                    </option>
                    {categories.map((c) => (
                        <option key={c.id} value={c.name}>
                            {c.name}
                        </option>
                    ))}
                </select>
                {errors.category && (
                    <p className="text-[11px] text-ctp-red mt-1">{errors.category.message}</p>
                )}
            </div>

            {/* Employee picker (only for salary category) */}
            {isSalary && currentEntity?.id && (
                <ClientSearchAutocomplete
                    key={autocompleteKey}
                    value={employeeName || ''}
                    label={t('cashier.createOperation.expenseEmployee', 'EMPLOYEE')}
                    placeholder={t('cashier.createOperation.expenseEmployeePlaceholder', 'Search employees...')}
                    isEmployee
                    onChange={(_id, name) => {
                        setValue('employeeName', name || '');
                        if (name) setEmployeeError(undefined);
                    }}
                    onClientSelect={(client: Client | null) => {
                        if (client) setValue('employeeName', client.name);
                    }}
                    onClearError={() => setEmployeeError(undefined)}
                    onCreateRequest={(name) => setCreateEmployeeFor(name)}
                    createLabel={t('cashier.createOperation.addEmployee', 'Add employee')}
                    error={employeeError}
                />
            )}

            {/* Date + Comment row */}
            <div className="flex flex-col sm:flex-row gap-4">
                {/* Date */}
                <div className="sm:w-44">
                    <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                        {t('cashier.createOperation.date', 'Date')}
                    </label>
                    <input
                        type="date"
                        className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-peach"
                        {...register('date')}
                    />
                    <p className="text-[11px] text-foreground-muted mt-1.5">
                        {t('cashier.createOperation.dateHint', 'Optional. Defaults to today.')}
                    </p>
                </div>

                {/* Comment */}
                <div className="flex-1">
                    <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                        {t('cashier.createOperation.comment', 'Comment')}
                    </label>
                    <input
                        type="text"
                        className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-peach placeholder:text-foreground-muted"
                        placeholder={t('cashier.createOperation.addNote', 'Add a note...')}
                        maxLength={200}
                        {...register('comment')}
                    />
                </div>
            </div>

            {/* TX Hash — only visible when selected wallet is crypto */}
            {selectedWallet && selectedWallet.type === 'crypto' && (
                <TxHashField
                    walletAddress={selectedWallet.address}
                    walletType={selectedWallet.type}
                    value={watch('txHash') || ''}
                    onChange={(v) => setValue('txHash', v)}
                />
            )}

            {/* Submit */}
            <button
                type="submit"
                disabled={isSubmitting || walletsLoading}
                className="
          flex items-center justify-center gap-2.5 w-full py-4 px-6
          text-white border-none rounded-xl text-[15px] font-bold
          cursor-pointer transition-all active:scale-95 mt-2
          shadow-lg
          bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600
          disabled:opacity-60 disabled:cursor-not-allowed
        "
            >
                <Receipt size={18} />
                {isSubmitting
                    ? t('cashier.createOperation.processing', 'Processing...')
                    : t('cashier.createOperation.expenseSubmit', 'RECORD EXPENSE')}
            </button>

            {/* Create employee modal (reuses client modal with isEmployee flag) */}
            {createEmployeeFor !== null && currentEntity?.id && (
                <CreateClientModal
                    entityId={currentEntity.id}
                    initialName={createEmployeeFor}
                    isEmployee
                    onClose={() => setCreateEmployeeFor(null)}
                    onSuccess={(client) => {
                        if (client) {
                            setValue('employeeName', client.name);
                            setEmployeeError(undefined);
                            setAutocompleteKey((k) => k + 1);
                        }
                        setCreateEmployeeFor(null);
                    }}
                />
            )}
        </form>
    );
}
